import * as THREE from 'three';
import GUI from 'lil-gui'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import particlesVertexShader from '../shaders/particles/vertex.glsl'
import particlesFragmentShader from '../shaders/particles/fragment.glsl'
import overlayVertexShader from '../shaders/overlay/vertex.glsl'
import overlayFragmentShader from '../shaders/overlay/fragment.glsl'
import gsap from 'gsap'



/**
 * Base
 */

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Debug
const gui = new GUI({ width: 340 })
gui.close()


// Loaders
let isResourcesLoaded = false;

// Loading Manager
const loadingManager = new THREE.LoadingManager(
    () => {
        const overlay = document.getElementById('loading-overlay')
        gsap.to(overlay, {
            duration: 2,
            opacity: 0,
            ease: 'power2.out',
            onComplete: () => {
                overlay.style.display = 'none'
            }
        });
    },
    (itemUrl, itemsLoaded, itemsTotal) => {
        
    },
    (error) => {
        console.error('Error loading resource:', error)
    }
);


const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('../draco/')

const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)



/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}
 

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Materials
    if (particles)
        particles.material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})



/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 8 * 2) //camera.position.z = 6
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true



/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))



/**
 * Particles
 */
//Load models
let particlesInitialized = false;
let particles = null
gltfLoader.load(
    '../models/moldels_1.glb',
    (gltf) => {
        particles = {}
        particles.index = 0

        //Positions
        const positions = gltf.scene.children.map((child) => {
            return child.geometry.attributes.position;
        });

        //console.log(positions);

        particles.maxCount = 0
        for(const position of positions)
        {
            if(position.count > particles.maxCount)
            {
                particles.maxCount = position.count
            }
        }
        //console.log(particles.maxCount)

        particles.positions = []
        for(const position of positions)
        {
            const originalArray = position.array
            const newArray = new Float32Array(particles.maxCount * 3)

            for(let i = 0; i < particles.maxCount; i++)
            {
                const i3 = i * 3

                if(i3 < originalArray.length)
                {
                    newArray[i3 + 0] = originalArray[i3 + 0]
                    newArray[i3 + 1] = originalArray[i3 + 1]
                    newArray[i3 + 2] = originalArray[i3 + 2]
                }
                else
                {
                    const randomIndex = Math.floor(position.count * Math.random()) * 3
                    newArray[i3 + 0] = originalArray[randomIndex + 0]
                    newArray[i3 + 1] = originalArray[randomIndex + 1]
                    newArray[i3 + 2] = originalArray[randomIndex + 2]
                   
                }
            }


            particles.positions.push(new THREE.Float32BufferAttribute(newArray, 3))
        }

        //console.log(particles.positions)


        // Geometry
        const sizesArray = new Float32Array(particles.maxCount)
        for(let i = 0; i < particles.maxCount; i++)
        {
            sizesArray[i] = Math.random()
        }

        particles.geometry = new THREE.BufferGeometry()
        particles.geometry.setAttribute('position', particles.positions[particles.index])        
        particles.geometry.setAttribute('aPositionTarget', particles.positions[2])
        particles.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizesArray, 1))
        
        
        // Material
        particles.colorA = new THREE.Color('#ff6030')
        particles.colorB = new THREE.Color('#1b3984')

        particles.material = new THREE.ShaderMaterial({
            vertexShader: particlesVertexShader,
            fragmentShader: particlesFragmentShader,
            uniforms:
            {
                uSize: new THREE.Uniform(0.2),
                uResolution: new THREE.Uniform(new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)),
                uProgress: new THREE.Uniform(0),
                uColorA: new THREE.Uniform(particles.colorA),
                uColorB: new THREE.Uniform(particles.colorB)
            },
            blending: THREE.AdditiveBlending,
            depthWrite: false,
        })
        
        // Points
        particles.points = new THREE.Points(particles.geometry, particles.material)
        particles.points.position.x = 4
        particles.points.scale.set(0.6, 0.6, 0.6)
        scene.add(particles.points)
        particlesInitialized = true;

        //Methods
        particles.morph = (index) =>
        {
            //update atributtes
            particles.geometry.attributes.position = particles.positions[particles.index]
            particles.geometry.attributes.aPositionTarget = particles.positions[index]

            //Animate uProgess
            gsap.fromTo(particles.material.uniforms.uProgress, { value: 0 }, { value: 1, duration: 3, ease: 'linear' })

            //Save index
            particles.index = index
        }
        particles.morph0 = () => { particles.morph(0) }
        particles.morph1 = () => { particles.morph(1) }
        particles.morph2 = () => { particles.morph(2) }
        particles.morph3 = () => { particles.morph(3) }

        //Tweaks
        gui.addColor(particles, 'colorA').name('Color A').onChange(() => { particles.material.uniforms.uColorA.value.set(particles.colorA) })
        gui.addColor(particles, 'colorB').name('Color B').onChange(() => { particles.material.uniforms.uColorB.value.set(particles.colorB) })
        gui.add(particles.material.uniforms.uSize, 'value').min(0).max(1).step(0.001).name('Size')
        gui.add(particles.material.uniforms.uProgress, 'value').min(0).max(1).step(0.001).name('Progress').listen()
        gui.add(particles, 'morph0').name('Morph 0')
        gui.add(particles, 'morph1').name('Morph 1')
        gui.add(particles, 'morph2').name('Morph 2')
        gui.add(particles, 'morph3').name('Morph 3')

    },
    undefined,
    (error) => {
        console.error('An error occurred while loading the model:', error);
    }
);



/**
 * Animate
 */
const clock = new THREE.Clock()
const tick = () => {
    const elapsedTime = clock.getElapsedTime()


    // Update controls
    controls.update()

  
    renderer.render(scene, camera);
   

    window.requestAnimationFrame(tick)
}
tick()

// Event listeners to header links
const headerLinks = document.querySelectorAll('header nav a');
headerLinks.forEach(link => {
    link.addEventListener('click', (event) => {
        event.preventDefault()
        const targetUrl = event.target.href

        // Start the overlay animation
        gsap.to('#white-overlay', {
            duration: 1,
            opacity: 1,
            onComplete: () => {
                window.location.href = targetUrl;
            }
        });
    });
});


// Fade-in effect on page load
window.addEventListener('load', () => {
    document.body.classList.add('loaded');
});



/**
 * Scroll Event
 */
window.addEventListener('scroll', () => {
    if (
        !particlesInitialized ||
        !particles ||
        !particles.positions ||
        particles.positions.length < 2 ||
        !particles.geometry.attributes.position ||
        !particles.geometry.attributes.aPositionTarget
    ) {
        return
    }

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    const scrollPosition = window.scrollY / scrollHeight

    const morphCount = particles.positions.length - 1
    const currentMorphIndex = Math.floor(scrollPosition * morphCount)
    const nextMorphIndex = Math.min(currentMorphIndex + 1, morphCount)

    const morphProgress = (scrollPosition * morphCount) % 1
    particles.points.position.x = 4

        // Update the particles geometry
    if (particles.positions[currentMorphIndex] && particles.positions[nextMorphIndex]) {
        particles.geometry.attributes.position = particles.positions[currentMorphIndex]
        particles.geometry.attributes.aPositionTarget = particles.positions[nextMorphIndex]
        particles.material.uniforms.uProgress.value = morphProgress

        particles.geometry.attributes.position.needsUpdate = true
        particles.geometry.attributes.aPositionTarget.needsUpdate = true
    }
});



//Event listener to mouse move
window.addEventListener('mousemove', (event) => {
    if (particlesInitialized) {
       
        const normalizedX = (event.clientX / window.innerWidth) - 0.5
        const normalizedY = (event.clientY / window.innerHeight) - 0.5

        particles.points.rotation.x = normalizedY * 0.2
        particles.points.rotation.y = normalizedX * 0.2
    }
});
